<template>
  <div class="equipment-profiles">

    <charging-settings-tabs ref="tabs" @profile-saved="load" />

    <v-card class="mb-4">
      <v-card-text class="d-flex align-center flex-wrap" style="gap: 12px">
        <div class="flex-grow-1">
          <div class="text-h6">{{ $t('onboarding.equipment-profile.title') }}</div>
          <div class="text-caption text--secondary">{{ $t('onboarding.equipment-profile.explained') }}</div>
        </div>
        <v-switch v-model="withArchived" dense hide-details class="mt-0 mr-4" :label="$t('onboarding.equipment-profile.show-archived')" @change="load" />
        <v-btn v-if="canManage" small color="primary" @click="openEdit()">
          <v-icon left small>mdi-plus</v-icon>{{ $t('onboarding.equipment-profile.new') }}
        </v-btn>
        <audit-trail-button entity-type="EquipmentProfile" :organization-id="$route.params.id" />
      </v-card-text>
    </v-card>

    <v-alert v-if="error" type="error" dense outlined class="mb-4">{{ error }}</v-alert>
    <v-skeleton-loader v-if="loading" type="card, card" />

    <template v-else>
      <v-alert v-if="!profiles.length" type="info" dense text>
        {{ $t('onboarding.equipment-profile.none') }}
        <v-btn v-if="canManage" small text color="primary" class="ml-1" @click="openEdit()">
          <v-icon left small>mdi-plus</v-icon>{{ $t('onboarding.equipment-profile.new') }}
        </v-btn>
      </v-alert>

      <!-- Un bloc par type d'équipement (un véhicule : ses deux services ensemble) -->
      <div v-for="group in groups" :key="group.family" class="mb-4">
        <div class="text-subtitle-2 mb-2">
          <v-icon small class="mr-1">{{ group.icon }}</v-icon>{{ group.label }}
        </div>
        <v-card v-for="profile in group.profiles" :key="profile.id" class="mb-2" :class="{ 'profile--archived': profile.archived }">
          <v-card-text class="d-flex align-center flex-wrap" style="gap: 8px">
            <div class="flex-grow-1">
              <div class="d-flex align-center flex-wrap" style="gap: 8px">
                <a v-if="canManage" class="text-subtitle-1 font-weight-medium profile__name" @click="openEdit(profile)">{{ profile.name }}</a>
                <span v-else class="text-subtitle-1 font-weight-medium">{{ profile.name }}</span>
                <v-chip x-small outlined color="primary">{{ isVehicle(profile.kind) ? $t(`onboarding.equipment-profile.service.${profile.kind}`) : $t(`onboarding.equipment-profile.usage.${profile.usage}`) }}</v-chip>
                <v-chip v-if="profile.archived" x-small>{{ $t('onboarding.equipment-profile.archived') }}</v-chip>
              </div>
              <div class="text-body-2 text--secondary mt-1">{{ licenceText(profile.licence) }}</div>
              <!-- Ses types de borne ou de compteur : tous, ou ceux qu'il vise -->
              <div v-if="deviceTypesOf(profile.kind).length" class="d-flex align-center flex-wrap mt-1" style="gap: 4px">
                <v-chip v-if="!(profile.deviceTypes || []).length" x-small>{{ $t(`onboarding.equipment-profile.device-types-all.${profile.kind}`) }}</v-chip>
                <v-chip v-for="deviceType in profile.deviceTypes || []" :key="deviceType" x-small>{{ deviceTypeLabel(deviceType) }}</v-chip>
              </div>
              <div v-if="profile.kind === 'charging-point'" class="text-caption text--secondary">
                {{ $t(`onboarding.equipment-profile.publication.${profile.usage}`) }}<template v-if="(profile.access || []).length"> · {{ accessText(profile.access) }}</template>
              </div>
            </div>
            <v-menu v-if="canManage" bottom left offset-y>
              <template #activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
              </template>
              <v-list dense>
                <v-list-item v-if="!profile.archived" @click="openEdit(profile)">
                  <v-list-item-title>{{ $t('onboarding.equipment-profile.edit') }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="openEdit(profile, true)">
                  <v-list-item-title>{{ $t('onboarding.equipment-profile.duplicate') }}</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="profile.archived" @click="update(profile, { archived: false }, 'onboarding.equipment-profile.restored-ok')">
                  <v-list-item-title>{{ $t('onboarding.equipment-profile.restore') }}</v-list-item-title>
                </v-list-item>
                <v-list-item v-else @click="update(profile, { archived: true }, 'onboarding.equipment-profile.archived-ok')">
                  <v-list-item-title class="error--text">{{ $t('onboarding.equipment-profile.archive') }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-card-text>
        </v-card>
      </div>
    </template>

    <!-- Créer ou modifier un profil : le même panneau que depuis la politique ou la mise en route -->
    <equipment-profile-editor
        v-if="canManage"
        v-model="editor.open"
        :organization-id="organizationId"
        :profile="editor.profile"
        :copy="editor.copy"
        @saved="saved"
    />
  </div>
</template>

<script>
import AuditTrailButton from "@elements/AuditTrailButton.vue";
import EquipmentProfileRepository from '@repository/EquipmentProfileRepository'
import MemberGroupRepository from '@repository/MemberGroupRepository'
import ChargingSettingsTabs from './ChargingSettingsTabs.vue'
import CommissioningMixin from './CommissioningMixin'
import EquipmentProfileEditor, { FAMILIES, VEHICLE_SERVICES } from './EquipmentProfileEditor.vue'
import { deviceTypesByKind } from './Commissioning/policyCatalogue'

/**
 * Les profils d'équipement d'une organisation (Réglages de recharge → Profils d'équipement) : un modèle
 * nommé par équipement (« Parking du siège », « Borne à domicile remboursée ») qui dit son usage et sa
 * licence — la seule de l'organisation pour ce genre d'équipement. Choisi à la commande ou à la pose ;
 * le modifier vaut pour les équipements à venir, ceux déjà posés gardent ce qu'ils ont.
 */
export default {
  name: 'EquipmentProfiles',

  components: {AuditTrailButton,  ChargingSettingsTabs, EquipmentProfileEditor },

  mixins: [CommissioningMixin],

  data() {
    return {
      profiles: [],
      loading: false,
      error: null,
      withArchived: false,
      memberGroups: [],
      deviceTypesMap: {},
      editor: { open: false, profile: null, copy: false },
    }
  },

  computed: {
    canManage() {
      return (this.currentUser?.permissions || []).includes('ONBOARDING_MODULE_MANAGE')
    },

    organizationId() {
      return this.$route.params.id
    },

    groups() {
      return FAMILIES.map(f => ({ family: f.family, icon: f.icon, label: this.familyLabel(f.family), profiles: this.profiles.filter(p => f.kinds.includes(p.kind)) }))
        .filter(g => g.profiles.length)
    },
  },

  created() {
    this.load()
    this.loadReferences()
  },

  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        this.profiles = await EquipmentProfileRepository.list(this.organizationId, this.withArchived)
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.loading = false
      }
    },

    /** Les groupes (pour dire qui charge où) et les sous-types (pour les nommer). */
    async loadReferences() {
      const [groups, deviceTypes] = await Promise.all([
        MemberGroupRepository.search({ organizationId: this.organizationId, offset: 0, limit: 200 }).catch(() => ({ items: [] })),
        deviceTypesByKind(),
      ])
      this.memberGroups = (groups.items || []).map(g => ({ id: g.id, name: g.name }))
      this.deviceTypesMap = deviceTypes || {}
    },

    deviceTypesOf(kind) {
      return this.deviceTypesMap[kind] || []
    },

    deviceTypeLabel(deviceType) {
      const key = `onboarding.device-type.${deviceType}`
      return this.$te(key) ? this.$t(key) : deviceType
    },

    /** « Abonnement · Remboursement · Formule » */
    licenceText(licence) {
      const typeKey = `onboarding.configuration.licence.type.${licence.licenceType}`
      return [licence.subscriptionName || this.$t('onboarding.equipment-profile.unknown-subscription'), this.$te(typeKey) ? this.$t(typeKey) : null, licence.formulaName]
        .filter(Boolean).join(' · ')
    },

    /** « Salariés (gratuit), Résidents (payant) » */
    accessText(access) {
      return access.map(grant => {
        const name = this.memberGroups.find(g => g.id === grant.groupId)?.name || '-'
        return `${name} (${this.$t(grant.free ? 'onboarding.equipment-profile.access-free' : 'onboarding.equipment-profile.access-paid').toLowerCase()})`
      }).join(', ')
    },

    /** Un profil à modifier, à dupliquer (`copy`), ou un nouveau. */
    openEdit(profile = null, copy = false) {
      this.editor = { open: true, profile, copy }
    },

    /** Enregistré : la liste, et la mise en route en tête de page. */
    async saved() {
      await this.load()
      this.$refs.tabs?.refresh()
    },

    isVehicle(kind) {
      return VEHICLE_SERVICES.includes(kind)
    },

    familyLabel(family) {
      return family === 'vehicle' ? this.$t('onboarding.equipment-profile.kind-vehicle') : this.kindLabel(family)
    },

    async update(profile, change, success) {
      try {
        await EquipmentProfileRepository.update(this.organizationId, profile.id, change)
        this.notifySuccess(success)
        await this.saved()
      } catch (e) {
        this.error = this.messageOf(e)
      }
    },
  },
}
</script>

<style scoped>
.profile__name {
  color: inherit;
}
.profile__name:hover {
  text-decoration: underline;
}
.profile--archived {
  opacity: 0.6;
}
</style>
